import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useHistory } from 'react-router-dom'
import { classNames } from 'primereact/utils';
import { CSSTransition } from 'react-transition-group';
import { Ripple } from 'primereact/ripple';
import { Tooltip } from 'primereact/tooltip';
import { Badge } from 'primereact/badge';

const AppSubmenu = (props: any) => {

    const [activeIndex, setActiveIndex] = useState<any>(null);
    const history = useHistory();
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | null>(null);
    const activeItemRef = useRef<HTMLLIElement>(null);
    const portalWrapperRef = useRef<HTMLUListElement>(null);

    const isHorizontal = useCallback(() => {
        return props.menuMode === 'horizontal';
    }, [props.menuMode]);
    const isSlim = useCallback(() => {
        return props.menuMode === 'slim';
    }, [props.menuMode]);

    const isHorizontalMode = isHorizontal();
    const isSlimMode = isSlim();

    const onMenuItemClick = (event: any, item: any, index: any) => {
        if (item.disabled) {
            event.preventDefault();
            return true;
        }

        if (props.root && props.onRootItemClick) {
            props.onRootItemClick({
                originalEvent: event,
                item: item
            });
        }

        //execute command
        if (item.command) {
            item.command({ originalEvent: event, item: item });
            event.preventDefault();
        }

        if (item.items) {
            setActiveIndex(activeIndex === index ? null : index);
        }
        else {
            const ink = getInk(event.currentTarget);
            if (ink) {
                removeClass(ink, 'p-ink-active');
            }
        }

        if (props.onMenuItemClick) {
            props.onMenuItemClick({
                originalEvent: event,
                item: item
            });
        }
    };

    const getInk = (el: any) => {
        for (let i = 0; i < el.children.length; i++) {
            if (typeof el.children[i].className === 'string' && el.children[i].className.indexOf('p-ink') !== -1) {
                return el.children[i];
            }
        }
        return null;
    }

    const removeClass = (element: any, className: string) => {
        if (element.classList)
            element.classList.remove(className);
        else
            element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }

    const onMenuItemMouseEnter = (index: any) => {
        if (props.root && props.menuActive && isHorizontalOrSlim() && !isMobile()) {
            setActiveIndex(index)
        }
    };

    const visible = (item: any) => {
        return typeof item.visible === "function" ? item.visible() : item.visible !== false;
    };

    const isHorizontalOrSlim = useCallback(() => {
        return (isHorizontalMode || isSlimMode);
    }, [isHorizontalMode, isSlimMode]);

    const isMobile = useCallback(() => {
        return window.innerWidth < 1025;
    }, []);

    useEffect(() => {
        if (!props.menuActive && (isHorizontalMode || isSlimMode) && !isMobile()) {
            setActiveIndex(null);
        }
    }, [props.menuActive, isHorizontalMode, isSlimMode, isMobile]);

    useLayoutEffect(() => {
        if (props.root && isHorizontalMode && activeIndex !== null && activeItemRef.current) {
            const updatePosition = () => {
                const rect = activeItemRef.current!.getBoundingClientRect();
                setDropdownStyle({
                    position: 'fixed',
                    top: rect.bottom,
                    left: rect.left,
                    zIndex: 1100,
                });
            };

            updatePosition();

            const menuContainer = activeItemRef.current.closest('.layout-menu');
            if (menuContainer) {
                menuContainer.addEventListener('scroll', updatePosition);
            }
            window.addEventListener('resize', updatePosition);

            return () => {
                if (menuContainer) {
                    menuContainer.removeEventListener('scroll', updatePosition);
                }
                window.removeEventListener('resize', updatePosition);
            };
        } else {
            setDropdownStyle(null);
        }
    }, [activeIndex, props.root, isHorizontalMode]);

    useEffect(() => {
        if (!props.root || !isHorizontalMode || activeIndex === null || !dropdownStyle) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            const isInsideActiveItem = activeItemRef.current && activeItemRef.current.contains(target);
            const isInsidePortal = portalWrapperRef.current && portalWrapperRef.current.contains(target);

            if (!isInsideActiveItem && !isInsidePortal) {
                setActiveIndex(null);
            }
        };

        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeIndex, props.root, isHorizontalMode, dropdownStyle]);

    const getLink = (item: any, index: any) => {
        const menuitemIconClassName = classNames('layout-menuitem-icon', item.icon);
        const badgeClassName = classNames('p-badge p-component p-badge-no-gutter', item.badgeClassName);
        const content = (
            <>
                <i className={menuitemIconClassName}></i>
                <span className="layout-menuitem-text">{item.label}</span>
                {item.badge && <Badge value={item.badge} style={item.badgeStyle} className={classNames(item.badgeClassName, "p-badge-no-gutter")} />}
                { item.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                <Ripple />
            </>
        );
        const commonLinkProps = {
            'style': item.style,
            'className': classNames(item.className, 'p-ripple tooltip', { 'p-disabled': item.disabled, 'p-link': !item.to }),
            'target': item.target,
            'onClick': (e: React.SyntheticEvent) => onMenuItemClick(e, item, index),
            'onMouseEnter': () => onMenuItemMouseEnter(index)
        }

        if (item.url) {
            return <a data-pr-tooltip={props.root && item.label} href={item.url} rel="noopener noreferrer" {...commonLinkProps}>{content}</a>
        }
        else if (!item.to) {
            return <a data-pr-tooltip={props.root && item.label} type="button" {...commonLinkProps}>{content}</a>
        }

        return <NavLink data-pr-tooltip={props.root && item.label} to={item.to} exact activeClassName="router-link-active" {...commonLinkProps}>{content}</NavLink>;
    };

    const getItems = () => {
        const transitionTimeout = props.root ? 0 : { enter: 1000, exit: 450 };
        return props.items.map((item: any, i: any) => {
            if (visible(item)) {
                const active = activeIndex === i;
                let styleClass = classNames(item.badgeStyleClass, { 'active-menuitem': active }, { 'layout-root-menuitem': props.root });
                const link = getLink(item, i);
                const isHorizontalRoot = props.root && isHorizontalMode;

                return (
                    <li
                        key={item.label || i}
                        className={styleClass}
                        role="menuitem"
                        ref={isHorizontalRoot && active ? activeItemRef : null}
                    >
                        {link}
                        {props.root && (
                            <div
                                onClick={(e) => {
                                    if (item.to) {
                                        onMenuItemClick(e, item, i);
                                        history.push(item.to);
                                    } else if (item.items) {
                                        setActiveIndex(activeIndex === i ? null : i);
                                    }
                                }}
                                style={{ cursor: item.to ? 'pointer' : 'default' }}
                            >
                                <span className="layout-menuitem-text" style={{ textTransform: 'uppercase' }}>{item.label}</span>
                            </div>
                        )}
                        {!(isHorizontalRoot && active && item.items) && (
                            <CSSTransition classNames="layout-submenu-container" timeout={transitionTimeout} in={item.items && (props.root && !((isHorizontalMode || isSlimMode) && !isMobile() && (!isSlimMode || (isSlimMode && activeIndex !== null))) ? true : active)} unmountOnExit>
                                <AppSubmenu items={visible(item) && item.items} onMenuItemClick={props.onMenuItemClick} menuMode={props.menuMode} menuActive={props.menuActive} parentMenuItemActive={active}></AppSubmenu>
                            </CSSTransition>
                        )}
                    </li>
                )
            }

            return null;
        })
    };

    const getPortalDropdown = () => {
        if (!props.root || !isHorizontalMode || activeIndex === null || !dropdownStyle) {
            return null;
        }

        const activeItem = props.items[activeIndex];
        if (!activeItem || !activeItem.items) {
            return null;
        }

            const renderMenuItem = (item: any, index: number) => {
                if (!visible(item)) {
                    return null;
                }

                const handleClick = (e: React.MouseEvent) => {
                    if (item.to) {
                        e.preventDefault();
                        history.push(item.to);
                    } else if (item.command) {
                        item.command({ originalEvent: e, item });
                    }
                };

                return (
                    <li key={item.label || index} className="layout-topbar-action-item">
                        <button
                            onClick={handleClick}
                            className="flex flex-row align-items-center p-link"
                        >
                            {item.icon && (
                                <i
                                    className={classNames("pi", item.icon)}
                                ></i>
                            )}
                            <span>{item.label}</span>
                        </button>
                    </li>
                );
            };

        const dropdown = (
            <ul
                ref={portalWrapperRef}
                className="layout-topbar-action-panel shadow-6"
                style={{
                    ...dropdownStyle,
                    position: 'fixed',
                    top: dropdownStyle.top,
                    left: dropdownStyle.left,
                    zIndex: 1100,
                }}
            >
                {activeItem.items.map((item: any, index: number) => renderMenuItem(item, index))}
            </ul>
        );

        return createPortal(dropdown, document.body);
    };

    if (!props.items) {
        return null;
    }

    const items = getItems();

    return (
        <>
            <ul role="menu" className={props.className}>{items}</ul>
            { getPortalDropdown() }
            { isSlim() && props.root && <Tooltip target="li:not(.active-menuitem)>.tooltip" />}
        </>
    );

}

const AppMenu = (props: any) => {

    return <AppSubmenu items={props.model} className="layout-menu"
        menuActive={props.active} onRootItemClick={props.onRootMenuItemClick}
        onMenuItemClick={props.onMenuItemClick} root={true} menuMode={props.menuMode}
        parentMenuItemActive={true} />

}

export default AppMenu;
